<!-- Multiselect.vue -->
<template>
  <div :id="id" class="multiselect">
    <AutoComplete
      ref="autocomplete"
      v-model="selectedValues"
      :suggestions="filteredOptions"
      @complete="search"
      @change="onSelectionChange"
      :completeOnFocus="true"
      :autoOptionFocus="true"
      :multiple="maxItems > 1"
      optionLabel="text"
      @keydown.esc.stop
      @keydown="handleKeyDown"
      @click="openDropdown"
      :placeholder="selectedValues.length > 0 ? '' : placeholder"
      v-bind="$attrs">

      <!-- Custom option template -->
      <template #option="{ option }">
        <div class="option">
          <div class="text-gray-900">
            {{ option.text }}
          </div>
          <div v-if="option.description" class="text-gray-500 text-xs">{{ option.description }}</div>
        </div>
      </template>
    </AutoComplete>

    <input type="hidden" :name="name" value="" v-if="selectedValues.length === 0">
    <select :name="name" :multiple="maxItems > 1" class="hidden" ref="hiddenSelect">
      <option v-for="item in selectedValuesArray" :key="item.value" :value="item.value" selected></option>
    </select>
  </div>
</template>

<!-- options should be an array of hashes: [ { text: ..., value: ... }, ...] -->

<script setup>
  import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
  import AutoComplete from 'primevue/autocomplete';

  const props = defineProps({
    id:                     { type: String, required: true },
    name:                   { type: String, required: true },
    options:                { type: Array, required: true },
    initialSelectedValues:  { type: Array, default: () => [] }, // should be an array of strings
    maxItems:               { type: Number, default: 1000 },
    placeholder:            { type: String, default: 'Select one or more' },
  })

  const name = props.name + ((props.maxItems > 1) ? '[]' : '')

  // This is unfiltered, and is a buffer so that it retains value when the options are changed
  const lastSelectedValues = ref(props.initialSelectedValues)

  const autocomplete = ref(null)

  // When props.options.length > 0, that means we're prepopulating the options,
  // which means initial values will be an array of integers.
  const initialValues = (props.options.length > 0)
    ? props.initialSelectedValues
        .map(value => props.options.find(opt => opt.value?.toString() === value?.toString()))
        .filter(item => item !== undefined)
    : props.initialSelectedValues

  // Need to convert to options format with { text: ..., value: ... } if they exist
  const selectedValues = ref(initialValues)
  const options = ref(props.options)
  const filteredOptions = ref([])
  const hiddenSelect = ref(null)

  // Should return an array of objects { text: ..., value: ... }
  const selectedValuesArray = computed(() => {
    if (!selectedValues.value) return [];

    let selectedValuesArray = Array.isArray(selectedValues.value) ? selectedValues.value : [selectedValues.value]

    selectedValuesArray = selectedValuesArray.map(value => {
      return options.value.find(opt => opt.value?.toString() === value?.value?.toString())
    }).filter(item => item !== undefined && item.value !== '' && item.value !== null)

    return selectedValuesArray
  })

  const search = (event) => {
    const query = event.query.toLowerCase()

    filteredOptions.value = options.value.filter(item => {
      const notSelected = !selectedValuesArray.value.some(selected => selected.value?.toString() === item.value?.toString())

      const matchesQuery =  (item.text.toLowerCase().includes(query) || item?.description?.toLowerCase().includes(query))

      return notSelected && matchesQuery
    })
  }

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      openDropdown(event)
    }
  }

  const openDropdown = (event) => {
    // Don't show dropdown if we've reached maxItems
    if (selectedValuesArray.value.length >= props.maxItems) {
      return
    }

    // Trigger the search to show all items
    if (filteredOptions.value.length === 0) {
      search({ query: '' });
    }

    nextTick(() => {
      if (autocomplete.value && autocomplete.value.show) {
        autocomplete.value.show();
      }
    });
  }

  const onSelectionChange = () => {
    search({ query:  '' })
  }

  // updateOptions might be called after selectedValues is already loaded from initialSelectedValues
  // in which case selectedValues.value is an array of integers (or strings) rather than an array of
  // { text: ..., value: ... }
  const updateOptions = (event) => {
    options.value = event.detail

    if (options.value.length && selectedValues.value.length) {
      const newSelectedValues = selectedValues.value
        .map(selectedValue => {
          if (typeof selectedValue === 'object') {
            return options.value.find(opt => opt.value?.toString() === selectedValue?.value?.toString())
          } else {
            return options.value.find(opt => opt.value?.toString() === selectedValue?.toString())
          }
        })
        .filter(item => item !== undefined)

      // nextTick() appears to be necessary because the AutoComplete might have trouble
      // updating both the options and the selection at the same time.  This way, options
      // are settled before the selectedValues are added back in.
      nextTick(() => {
        selectedValues.value = newSelectedValues
      })
    }

    search({ query: '' })
  }

  const updateSelectedValues = (event) => {
    // Clear initialSelectedValues when we manually override
    selectedValues.value = event.detail
      .map(value => props.options.find(opt => opt.value?.toString() === value?.toString()))
      .filter(item => item !== undefined)

    search({ query: '' })
  }

  // Propagate the change event to the hidden select
  watch(selectedValues, () => {
    nextTick(() => {
      if (hiddenSelect.value) {
        hiddenSelect.value.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
  }, { deep: true })

  // props.options should not change on its own; all changes to options should be done via custom event

  onMounted(async () => {
    const el = document.getElementById(props.id)
    if (el) {
      el.addEventListener('setOptions', updateOptions)
      el.addEventListener('update:modelValue', updateSelectedValues)
    }

    await nextTick();

    const renderedElement = document.getElementById(props.id)
    if (renderedElement) {
      renderedElement.dispatchEvent(new CustomEvent('multiselect:ready', {
        detail: {
          element: renderedElement,
        },
        bubbles: true
      }));
    }
  })

  onUnmounted(() => {
    const el = document.getElementById(props.id)
    if (el) {
      el.removeEventListener('setOptions', updateOptions)
      el.removeEventListener('update:modelValue', updateSelectedValues)
    }
  })
</script>
